import {IPlayer, Player} from "@game/player/player";
import {Game} from "@game/game";
import {Region} from "@game/map/region";
import {EventHandler} from "@utility/eventHandler";
import {GameEvent} from "@game/gameEvent";
import {Hand} from "@game/player/hand";
import {Vector2} from "@utility/vector2";
import {Letter} from "@game/map/letter";
import {Tile} from "@game/map/tile";
import {Score} from "@game/player/score";

export interface IPlayerPollData {
    hand: string[];
    location: { regionId: number; tileId: number } | null;
    shovels: number;
    score?: number;
}

interface IUserPlayer extends IPlayer {
    game: Game
    hand: string[]
    nickname: string
    nickNumber: number
    username: string
    score: number
    shovels: number
}
export class UserPlayer extends Player {
    private readonly _events = new EventHandler<GameEvent>();
    private readonly _game: Game;
    private readonly _score: Score;

    private _hand: Hand;
    private _nearbyPlayers: Player[] = [];
    private _nearbyRegions: Record<number, Region> = {};
    private _playerRegionIds = new Set<number>();
    private _viewportRegionIds = new Set<number>();
    private _nickname: string
    private _nickNumber: number
    private _updateUserPlayerChecksum = "";
    private _username: string = "";

    constructor(props: IUserPlayer) {
        super(props);
        this._game = props.game;
        this._hand = new Hand({letters: props.hand, player: this.playerId, shovels: props.shovels});
        this._nickname = props.nickname;
        this._nickNumber = props.nickNumber;
        this._score = new Score({lifetime: props.score});
        this._username = props.username;
    }

    get anonymous() {
        return this._username.match("@") === null;
    }

    get username() {
        return this._username;
    }

    get events() {
        return this._events;
    }

    get hand() {
        return this._hand;
    }

    get nearbyRegions() {
        return this._nearbyRegions;
    }

    get score() {
        return this._score;
    }

    getNearbyRegionIds() {
        return Object.keys(this._nearbyRegions).map(Number);
    }

    getNearestLetterTiles(count: number) {
        return this.getNearestTiles(count, tile => tile.letter !== undefined);
    }

    getNearestPlayerTiles(count: number) {
        const myPosition = this.getPosition(this._game.size);
        const sorted = this._nearbyPlayers
            .map(player => ({ player, rangeSquared: player.getPosition(this._game.size).minus(myPosition).lengthSquared() }))
            .sort((a, b) => a.rangeSquared - b.rangeSquared);
        return sorted.slice(0, count).map(c => c.player);
    }

    updateNearbyPlayers(data: { playerId: string; regionId: number; tileId: number }[]) {
        this._nearbyPlayers = data.map(p => new Player({
            playerId: p.playerId,
            location: { regionId: p.regionId, tileId: p.tileId }
        }));
        this.events.update(new GameEvent("NearbyPlayersUpdated", {
            playerIds: data.map(p => p.playerId)
        }));
    }

    getNearestTiles(count: number, predicate: (tile: Tile) => boolean) {
        const closestTiles: {tile: Tile, rangeSquared: number}[] = [];
        let lowestRangeSquared: number = - Number.MAX_VALUE;

        Object.values(this._nearbyRegions).forEach(region => region.tiles.forEach(tile => {
            if (predicate(tile)) {
                const rangeSquared = tile.position.minus(this.getPosition(this._game.size)).lengthSquared();

                if (closestTiles.length < count) {
                    closestTiles.push({tile, rangeSquared});
                    lowestRangeSquared = Math.max(lowestRangeSquared, rangeSquared);
                } else if (rangeSquared < lowestRangeSquared) {
                    const index = closestTiles.findIndex(closest =>
                        closest.rangeSquared === lowestRangeSquared);

                    closestTiles.splice(index, 1, {tile, rangeSquared});
                    lowestRangeSquared = closestTiles.reduce((lowest, compare) =>
                        Math.max(compare.rangeSquared, lowest), -1);
                }
            }
        }));
        return closestTiles.map(closest => closest.tile);
    }

    getNickname() {
        return Player.MakeNickname(this._nickname, this._nickNumber);
    }

    hasNickname() {
        return this._nickNumber && this._nickname;
    }

    async initialise() {
        await this.updateNearbyRegions();
        this.events.update(new GameEvent("Initialised"));
    }

    /** Called by DataSubscriptionManager on each poll cycle. */
    async updateFromPoll(data: IPlayerPollData): Promise<void> {
        if (await this.handlePlayerUpdate(data)) {
            this.events.update(new GameEvent("Updated"));
        }
    }

    dragPlacedLetter(letter: Letter) {
        if (this.hand.dragPlacedLetter(letter)) {
            if (letter.regionId !== undefined && letter.tileId !== undefined) {
                const region = this._nearbyRegions[letter.regionId];
                const tile = region.tiles[letter.tileId];

                tile.updateLetter(undefined);
                this._game.removePlacedLetter(letter);
                this._events.update(new GameEvent("MapChanged"));
                return true;
            }
        }
        return false;
    }

    move(position: Vector2) {
        this._location = this._game.size.getTileLocation(position);
        return this.updateNearbyRegions();
    }

    async placeLetters() {
        await this._game.placeLetters(this.hand.placed);
    }

    placeDraggedLetter(position: Vector2) {
        const dragged = this._hand.dragging;

        if (dragged) {
            const size = this._game.size;
            const regionId = size.getRegionId(size.getTileRegion(position));
            const tileId = size.getRegionTileId(position);
            const region = this._nearbyRegions[regionId];
            const tile = region.tiles[tileId];

            if (region && tile.letter === undefined && ! tile.place?.blocking) {
                region.placeLetter(dragged, this._game.size.getTileId(size.getTileRegionOffset(position)));
                this._game.placeLetter(dragged, position);
                this._hand.placeDraggedLetter();
                this._events.update(new GameEvent("MapChanged"));
                return true;
            }
        }
        return false;
    }

    resetPlacedLetters() {
        this.hand.dropLetter(-1);
        this.hand.placed.forEach(letter => {
            if (letter.regionId !== undefined && letter.tileId !== undefined) {
                delete this.nearbyRegions[letter.regionId].letters[letter.tileId];
                this.nearbyRegions[letter.regionId].tiles[letter.tileId].updateLetter(undefined);
            }
        });
        this.hand.resetPlacedLetters();
        this._game.resetPlacedLetters();
        this.events.update(new GameEvent("ResetPlacedLetters"))
    }

    async selectNickname(nickname: string) {
        interface ISelectNickname {
            nickname: string
            nickNumber: number
        }

        let result = await this._game.host.cloud<ISelectNickname>("selectNickname", {
            nickname
        });

        this._nickname = result.nickname;
        this._nickNumber = result.nickNumber;
        return this.events.update(new GameEvent("SelectedNickname", result));
    }

    private async handlePlayerUpdate(update: IPlayerPollData): Promise<boolean> {
        const checksum = JSON.stringify(update);

        if (this._updateUserPlayerChecksum === checksum) return false;

        // Don't overwrite hand state while the player has letters placed on the board
        // or is actively dragging — the server doesn't know about in-flight placements.
        if (this._hand.placed.length === 0 && !this._hand.dragging) {
            this._hand.updateLetters(update.hand);
            this._hand.updateShovels(update.shovels);
            this._hand.events.update(new GameEvent("Updated"));
        }

        if (update.location) {
            this._location = update.location;
        }
        this._updateUserPlayerChecksum = checksum;
        await this.updateNearbyRegions();
        return true;
    }
    
    private handleRegionUpdate(gameEvent: GameEvent) {
        this._events.update(new GameEvent("RegionUpdated", gameEvent.properties));
    }

    async updateViewportRegions(tileCenter: Vector2) {
        const regionIds = this._game.geography.getRegionIdsAround(this._game, tileCenter);
        const hasNewRegions = regionIds.some(id => this._nearbyRegions[id] === undefined);

        this._viewportRegionIds = new Set(regionIds);
        await this._syncRegions();
        if (hasNewRegions) {
            this._game.poll().catch(console.error);
        }
    }

    private async updateNearbyRegions() {
        const position = this.getPosition(this._game.size);
        this._playerRegionIds = new Set(this._game.geography.getRegionIdsAround(this._game, position));
        await this._syncRegions();
    }

    private async _syncRegions() {
        const allNeededIds = new Set(
            Array.from(this._playerRegionIds).concat(Array.from(this._viewportRegionIds))
        );
        let updated = false;

        Object.keys(this._nearbyRegions).map(Number).forEach(regionId => {
            if (!allNeededIds.has(regionId)) {
                updated = true;
                this._nearbyRegions[regionId].destroy();
                this._nearbyRegions[regionId].events.delegates.remove(event =>
                    this.handleRegionUpdate(event));
                delete this._nearbyRegions[regionId];
            }
        });
        for (const regionId of Array.from(allNeededIds)) {
            if (this._nearbyRegions[regionId] === undefined) {
                const region = new Region({regionId, game: this._game});

                updated = true;
                region.events.delegates.add(event => this.handleRegionUpdate(event));
                this._nearbyRegions[region.regionId] = region;
                await region.initialise();
            }
        }
        updated && this.events.update(new GameEvent("UpdateNearbyRegions"));
    }
}